import { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useToast,
} from "@chakra-ui/react";
import { getFunctions, httpsCallable } from "firebase/functions";
import type { Product } from "../types";

interface InquiryModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
}

const InquiryModal = ({ product, isOpen, onClose }: InquiryModalProps) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [isSending, setIsSending] = useState(false);
  const toast = useToast();

  const reset = () => {
    setName("");
    setPhone("");
    setNote("");
  };

  const handleSend = async () => {
    setIsSending(true);
    const inquiry = {
      productName: product.name,
      productCategory: product.category,
      productPrice: product.price,
      name,
      phone,
      note,
    };

    try {
      const functions = getFunctions();
      const sendInquiry = httpsCallable(functions, "sendInquiryEmail");
      await sendInquiry(inquiry);
      toast({
        title: "Inquiry sent",
        description: "We'll get back to you shortly.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      reset();
      onClose();
    } catch (err) {
      console.error("Failed to send inquiry:", err);
      toast({
        title: "Couldn't send inquiry",
        description: "Please try again or contact us directly.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        bg="rs.surface"
        border="1px solid"
        borderColor="rs.borderStrong"
        borderRadius="10px"
      >
        <ModalHeader
          color="rs.text"
          fontFamily="'DM Serif Display', serif"
          fontWeight="400"
        >
          Enquire about this item
        </ModalHeader>
        <ModalCloseButton color="rs.textMuted" />
        <ModalBody pb={6}>
          <Box
            mb={4}
            p={3}
            bg="rs.bg"
            borderRadius="6px"
            border="1px solid"
            borderColor="rs.border"
          >
            <Text fontSize="13px" fontWeight="500" color="rs.text">
              {product.name}
            </Text>
            <Text fontSize="12px" color="rs.textMuted" mt="2px">
              {product.price.toLocaleString("en-LK")} LKR
            </Text>
          </Box>

          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="12px" color="rs.textMuted">
                Name
              </FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                bg="rs.bg"
                borderColor="rs.border"
                color="rs.text"
                _hover={{ borderColor: "rs.borderStrong" }}
                _focus={{ borderColor: "rs.red", boxShadow: "none" }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="12px" color="rs.textMuted">
                Phone number
              </FormLabel>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="07X XXX XXXX"
                bg="rs.bg"
                borderColor="rs.border"
                color="rs.text"
                _hover={{ borderColor: "rs.borderStrong" }}
                _focus={{ borderColor: "rs.red", boxShadow: "none" }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="12px" color="rs.textMuted">
                Note / message
              </FormLabel>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Any specific questions about this item..."
                bg="rs.bg"
                borderColor="rs.border"
                color="rs.text"
                rows={4}
                _hover={{ borderColor: "rs.borderStrong" }}
                _focus={{ borderColor: "rs.red", boxShadow: "none" }}
              />
            </FormControl>

            <Button
              onClick={handleSend}
              isDisabled={!name || !phone}
              isLoading={isSending}
              loadingText="Sending..."
              w="100%"
            >
              Send inquiry
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default InquiryModal;
